import React, { useMemo, useState } from 'react';
import {
  getBrandById,
  HOLDING_BRAND_NAMES,
  HOLDING_STORE_MENU,
} from '../constants/holdingBrands';
import './BrandLandingPage.css';

const WAITLIST_KEY = 'javiland_brand_waitlist';
const HUB_INSTAGRAM = 'https://www.instagram.com/javilandoficial/';

function saveWaitlistLocal(brandId, email) {
  try {
    const raw = localStorage.getItem(WAITLIST_KEY);
    const list = raw ? JSON.parse(raw) : [];
    list.push({ brandId, email, at: new Date().toISOString() });
    localStorage.setItem(WAITLIST_KEY, JSON.stringify(list.slice(-200)));
  } catch {
    /* ignore */
  }
}

const NETWORK_LABEL = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
};

/**
 * Landing por marca — diseño comercial: live → canal; pronto → waitlist + redes + live siblings.
 */
const BrandLandingPage = ({ brandId }) => {
  const brand = getBrandById(brandId) || HOLDING_BRAND_NAMES.saludable;
  const isLive = brand.status === 'live' && brand.storeUrl;
  const social = brand.social || {};
  const socialEntries = Object.entries(social).filter(([, url]) => url && String(url).trim());
  const highlights = brand.highlights || [];
  const pitch = brand.pitch || brand.tagline;

  const liveSiblings = useMemo(
    () => HOLDING_STORE_MENU.filter((b) => b.id !== brand.id && b.status === 'live'),
    [brand.id]
  );

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const go = (url) => {
    if (!url) return;
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const onWaitlist = (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setSubmitting(true);
    setError(null);
    try {
      saveWaitlistLocal(brand.id, value);
      setDone(true);
      setEmail('');
    } catch {
      setError('No se pudo registrar. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={`brand-landing${isLive ? ' brand-landing--live' : ' brand-landing--soon'}`}>
      <div className="brand-landing-hero" aria-hidden="true">
        <div className="brand-landing-hero-geo" />
      </div>

      <div className="brand-landing-inner">
        <p className="brand-landing-eyebrow">Javiland · Proyectos digitales</p>
        <h1 className="brand-landing-title">{brand.name}</h1>
        {brand.provisional && (
          <p className="brand-landing-provisional">Nombre provisional</p>
        )}
        <p className="brand-landing-tagline">{brand.tagline}</p>
        <p className="brand-landing-body">{pitch}</p>

        {highlights.length > 0 && (
          <ul className="brand-landing-chips">
            {highlights.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
        )}

        {isLive ? (
          <div className="brand-landing-panel">
            <p className="brand-landing-meta">{brand.domainNote}</p>
            <div className="brand-landing-actions">
              <button type="button" className="brand-landing-cta" onClick={() => go(brand.storeUrl)}>
                Entrar a {brand.name}
              </button>
              {brand.storeUrl !== '/tienda' && (
                <button type="button" className="brand-landing-cta brand-landing-cta--ghost" onClick={() => go('/tienda')}>
                  Ver tienda Costal
                </button>
              )}
            </div>
            {socialEntries.length > 0 && (
              <div className="brand-landing-social">
                <p className="brand-landing-social-label">Seguí {brand.name}</p>
                <ul className="brand-landing-social-list">
                  {socialEntries.map(([network, url]) => (
                    <li key={network}>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {network === 'instagram' && <i className="fab fa-instagram" aria-hidden />}
                        {network === 'facebook' && <i className="fab fa-facebook" aria-hidden />}
                        {network === 'linkedin' && <i className="fab fa-linkedin" aria-hidden />}
                        <span>{NETWORK_LABEL[network] || network}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="button" className="brand-landing-link" onClick={() => go('/proyectos')}>
              Ver todos los proyectos
            </button>
          </div>
        ) : (
          <div className="brand-landing-panel">
            <p className="brand-landing-soon-badge">Ecosistema en construcción</p>
            <p className="brand-landing-meta">{brand.domainNote}</p>

            {done ? (
              <p className="brand-landing-waitlist-ok">
                Gracias — te avisaremos cuando {brand.name} abra.
              </p>
            ) : (
              <form className="brand-landing-waitlist" onSubmit={onWaitlist}>
                <label className="brand-landing-waitlist-label" htmlFor={`waitlist-${brand.id}`}>
                  Quiero saber de {brand.name}
                </label>
                <div className="brand-landing-waitlist-row">
                  <input
                    id={`waitlist-${brand.id}`}
                    type="email"
                    required
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    autoComplete="email"
                  />
                  <button type="submit" className="brand-landing-cta" disabled={submitting}>
                    {submitting ? '…' : 'Avisarme'}
                  </button>
                </div>
                {error && <p className="brand-landing-waitlist-error">{error}</p>}
              </form>
            )}

            <div className="brand-landing-social">
              <p className="brand-landing-social-label">
                {socialEntries.length > 0 ? `Redes ${brand.name}` : 'Seguí el lanzamiento'}
              </p>
              <ul className="brand-landing-social-list">
                {socialEntries.map(([network, url]) => (
                  <li key={network}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {network === 'instagram' && <i className="fab fa-instagram" aria-hidden />}
                      {network === 'facebook' && <i className="fab fa-facebook" aria-hidden />}
                      {network === 'linkedin' && <i className="fab fa-linkedin" aria-hidden />}
                      <span>{NETWORK_LABEL[network] || network}</span>
                    </a>
                  </li>
                ))}
                <li>
                  <a href={HUB_INSTAGRAM} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram" aria-hidden />
                    <span>Javiland · hub</span>
                  </a>
                </li>
              </ul>
            </div>

            {liveSiblings.length > 0 && (
              <div className="brand-landing-siblings">
                <p className="brand-landing-social-label">Mientras, explorá marcas live</p>
                <div className="brand-landing-actions">
                  {liveSiblings.map((sib) => (
                    <button
                      key={sib.id}
                      type="button"
                      className="brand-landing-cta brand-landing-cta--ghost"
                      onClick={() => go(sib.menuPath || sib.storeUrl || sib.hubPath)}
                    >
                      {sib.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button type="button" className="brand-landing-link" onClick={() => go('/proyectos')}>
              Ver todos los proyectos
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default BrandLandingPage;
